/**
 * Landscape stages - a single source of truth for the UI.
 *
 * Four stages in a fixed order: DEV, QAS, PRE-PROD, PROD. A space may use two, three, or
 * all four of them. Transport always moves forward, and a stage the space does not have
 * is skipped rather than blocking the transport.
 *
 * Never compare on the order number directly. Before PRE-PROD existed as a stage,
 * `stageOrder === 2` meant PROD; now that PRE-PROD sits in between, 2 is the stage before
 * production and 3 is production. Any comparison against the bare number silently points
 * at the wrong landscape. `order` exists only for sorting and for the forward-stage check;
 * the stage `key` carries the actual meaning.
 *
 * The values mirror STAGES in backend/server.js. The backend returns `stage` and
 * `stageOrder` on every instance response; this file only holds the labels and the
 * rules built on top of them.
 */

export const STAGES = [
  { key: "dev", order: 0, label: "DEV", env: "DEV" },
  { key: "qas", order: 1, label: "QAS", env: "QAS" },
  { key: "preprod", order: 2, label: "PRE-PROD", env: "PRE-PROD" },
  { key: "prod", order: 3, label: "PROD", env: "PROD" },
];

export const STAGE_KEYS = STAGES.map(s => s.key);

/**
 * Tag spelling -> stage. Mirrors STAGE_ORDER_MAP in backend/server.js.
 * Aliases used by existing data stay valid: "test"/"tst"/"qa" still count as QAS,
 * so no existing tag has to be touched.
 */
export const TAG_ZU_STUFE = {
  dev: "dev", develop: "dev", development: "dev",
  qas: "qas", qa: "qas", tst: "qas", test: "qas",
  preprod: "preprod", "pre-prod": "preprod", pre_prod: "preprod", pp: "preprod",
  stage: "preprod", staging: "preprod", preproduction: "preprod",
  prod: "prod", prd: "prod", production: "prod", live: "prod",
};

/** Derives the stage from tags, same rule as the backend. */
export function stufeAusTags(tags) {
  for (const t of (Array.isArray(tags) ? tags : [])) {
    const k = String(t || "").trim().toLowerCase();
    if (TAG_ZU_STUFE[k]) return TAG_ZU_STUFE[k];
  }
  return null;
}

/** Label for a stage. An unknown key is upper-cased rather than swallowed. */
export function stageLabel(key) {
  return STAGES.find(s => s.key === key)?.label || String(key || "").toUpperCase();
}

export function stageOrderOf(key) {
  const s = STAGES.find(x => x.key === key);
  return s ? s.order : null;
}

/**
 * Production-like means PROD and PRE-PROD. PRE-PROD carries real data for many customers,
 * so the same warnings apply there as in PROD.
 */
export function istProdNah(key) {
  return key === "prod" || key === "preprod";
}

/**
 * The stages of a space in order, only the ones that actually exist.
 * instanzen: objects with a `stage` field (from the API).
 */
export function stufenDesSpace(instanzen) {
  const vorhanden = new Set((instanzen || []).map(i => i && i.stage).filter(Boolean));
  return STAGES.filter(s => vorhanden.has(s.key));
}

/**
 * The next stage forward that actually exists. Missing stages are skipped: a space with
 * DEV + PRE-PROD + PROD transports from DEV straight to PRE-PROD.
 * Returns null when there is no later stage (the final stage does not transport onward).
 */
export function naechsteStufe(vorhandeneKeys, vonKey) {
  const von = STAGES.find(s => s.key === vonKey);
  if (!von) return null;
  const treffer = STAGES
    .filter(s => s.order > von.order && (vorhandeneKeys || []).includes(s.key))
    .sort((a, b) => a.order - b.order);
  return treffer.length ? treffer[0].key : null;
}

/**
 * The last stage before PROD that the space actually has: the staging point for the
 * release candidate. For DEV/QAS/PRE-PROD/PROD that is PRE-PROD, for DEV/QAS/PROD it is QAS.
 * Returns null when there is no PROD, or no stage before it.
 */
export function letzteStufeVorProd(vorhandeneKeys) {
  const vorhanden = vorhandeneKeys || [];
  if (!vorhanden.includes("prod")) return null;
  const prodOrder = stageOrderOf("prod");
  const davor = STAGES
    .filter(s => s.order < prodOrder && vorhanden.includes(s.key))
    .sort((a, b) => b.order - a.order);
  return davor.length ? davor[0].key : null;
}
